import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GitMerge, AlertTriangle } from "lucide-react";

const ETIQUETAS = {
  tareas: "tareas en trabajos", cronogramas: "referencias en cronogramas",
  solicitudes: "solicitudes de servicio", servicios_padre: "relaciones padre/hijo",
};

export function ServicioFusionDialog({ abierto, ids, onCerrar, onFusionado }) {
  const [cargando, setCargando] = useState(false);
  const [servicios, setServicios] = useState([]);
  const [destinoId, setDestinoId] = useState(null);
  const [nombre, setNombre] = useState("");
  const [resumen, setResumen] = useState(null);
  const [ejecutando, setEjecutando] = useState(false);

  useEffect(() => {
    if (!abierto || !ids?.length) return;
    setCargando(true);
    setResumen(null);
    api.get("/servicios/referencias", { params: { ids: ids.join(",") } })
      .then((r) => {
        const lista = r.data.servicios || [];
        setServicios(lista);
        const conPlantillas = lista.filter((s) => s.tiene_plantillas);
        const mejor = conPlantillas[0] || [...lista].sort((a, b) => b.total - a.total)[0];
        if (mejor) { setDestinoId(mejor.id); setNombre(mejor.nombre || ""); }
      })
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [abierto, ids]);

  const elegirDestino = (s) => { setDestinoId(s.id); setNombre(s.nombre || ""); };
  const origenIds = servicios.filter((s) => s.id !== destinoId).map((s) => s.id);
  const destino = servicios.find((s) => s.id === destinoId);
  const alertaPlantillas = destino && !destino.tiene_plantillas &&
    servicios.some((s) => s.id !== destinoId && s.tiene_plantillas);

  const cargarResumen = useCallback(() => {
    if (!destinoId || !origenIds.length) { setResumen(null); return; }
    api.get("/servicios/referencias", { params: { ids: origenIds.join(",") } })
      .then((r) => setResumen(r.data.combinado || {}))
      .catch(() => setResumen(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoId, servicios]);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const partesResumen = () => {
    if (!resumen) return "";
    return Object.entries(resumen).filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} ${ETIQUETAS[k] || k}`).join(", ") || "sin referencias asociadas";
  };

  const fusionar = async () => {
    if (!destinoId || origenIds.length < 1) { toast.error("Elija el servicio que se conserva"); return; }
    let texto = `Se unirán ${servicios.length} servicios en "${(nombre || "").trim()}". ` +
      `Se reasignarán ${partesResumen()}. Los otros ${origenIds.length} servicio(s) se eliminarán. ` +
      `Esta acción no se puede deshacer.`;
    if (alertaPlantillas) {
      texto = "AVISO: el servicio destino no tiene plantillas asociadas; se conservarán las de un origen para no perderlas.\n\n" + texto;
    }
    if (!window.confirm(texto)) return;
    setEjecutando(true);
    try {
      const { data } = await api.post("/servicios/fusionar", { destino_id: destinoId, origen_ids: origenIds, nombre });
      toast.success(`Servicios fusionados en "${data.destino.nombre}" (${data.eliminados} eliminados)`);
      onFusionado?.();
      onCerrar?.();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setEjecutando(false); }
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) onCerrar?.(); }}>
      <DialogContent data-testid="servicio-fusion-dialog" className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge size={18} /> Fusionar servicios</DialogTitle>
          <DialogDescription>Elija el servicio que se conserva; los demás se reasignarán a él y se eliminarán.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3">
          {cargando && <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>}
          {!cargando && servicios.map((s) => {
            const esDestino = s.id === destinoId;
            return (
              <label key={s.id} data-testid={`fusion-servicio-${s.id}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${esDestino ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="destino-srv" data-testid={`fusion-servicio-radio-${s.id}`}
                  checked={esDestino} onChange={() => elegirDestino(s)} className="mt-1 accent-indigo-600" />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{s.nombre}</span>
                    {esDestino && <span className="text-[10px] font-bold uppercase text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">Se conserva</span>}
                    {s.tiene_plantillas && <span className="text-[10px] font-bold uppercase text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">{s.num_plantillas} plantilla(s)</span>}
                    {!s.activo && <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Inactivo</span>}
                    {s.solo_cronograma && <span className="text-[10px] font-bold uppercase text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Solo cronograma</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    <span data-testid={`fusion-servicio-total-${s.id}`}>{s.total} registros dependen</span>
                  </div>
                </div>
              </label>
            );
          })}

          {!cargando && destinoId && (
            <div className="space-y-1 pt-1">
              <label className="text-xs font-medium text-slate-500">Nombre final del servicio</label>
              <Input data-testid="fusion-servicio-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </div>
          )}

          {!cargando && alertaPlantillas && (
            <div data-testid="fusion-servicio-alerta-plantillas" className="flex items-start gap-2 text-sm bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              El servicio destino no tiene plantillas asociadas. Se conservarán las de un origen para no perderlas. Se recomienda elegir como destino el que ya tiene plantillas.
            </div>
          )}

          {!cargando && destinoId && (
            <div data-testid="fusion-servicio-resumen" className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              Se reasignarán <b>{partesResumen()}</b> al servicio <b>{(nombre || "").trim()}</b> y se eliminarán {origenIds.length} servicio(s). Esta acción no se puede deshacer.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 shrink-0">
          <Button variant="outline" data-testid="fusion-servicio-cancelar-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => onCerrar?.()}>Cancelar</Button>
          <Button data-testid="fusion-servicio-confirmar-btn" onClick={fusionar}
            disabled={ejecutando || cargando || !destinoId || origenIds.length < 1}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
            {ejecutando ? "Fusionando…" : "Fusionar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
